import { FC } from 'react';
import { MetricsSelectorUI } from './MetricsSelectorUI';
import { useMetricsSelection } from './useMetricsSelection';
import type { MetricOption } from './MetricsSelectorUI';

export interface MetricsSelectorConfig<T extends string> {
  metrics: MetricOption<T>[];
  defaultMetrics: T[];
  iconClassName?: string;
}

export interface MetricsSelectorProps<T extends string> {
  initialSelected?: T[];
  onApply: (selected: T[]) => void;
  onClose?: () => void;
}

/**
 * Factory function to create a MetricsSelector component with specific configuration
 * Eliminates duplication between Dashboard and Analytics MetricsSelector components
 */
export const createMetricsSelector = <T extends string>(config: MetricsSelectorConfig<T>): FC<MetricsSelectorProps<T>> => {
  const MetricsSelector: FC<MetricsSelectorProps<T>> = ({ initialSelected = [], onApply, onClose }) => {
    const { selected, handleToggle, handleReset, isMaxSelected } = useMetricsSelection(initialSelected);

    return (
      <MetricsSelectorUI
        selected={selected}
        metrics={config.metrics}
        isMaxSelected={isMaxSelected}
        onToggle={handleToggle}
        onReset={() => handleReset(config.defaultMetrics)}
        onApply={() => {
          onApply(selected);
          if (onClose) onClose();
        }}
        iconClassName={config.iconClassName}
      />
    );
  };

  return MetricsSelector;
};
